import React, { useState, useEffect, useRef } from 'react';
import { playPhaseStart, playWarning, playTimeUp, playClick, playVictory } from '../sounds';

const HEBREW_LETTERS = ['א','ב','ג','ד','ה','ו','ז','ח','ט','י','כ','ל','מ','נ','ס','ע','פ','צ','ק','ר','ש','ת'];
const P = '#8B5CF6', G = '#34D399', R = '#FB7185', Y = '#FCD34D', M = '#A8B3CF', BG = '#0C0A1D';

function CountryCity({ gameState, playerId, roomCode, sendMessage, error, clearError }) {
  const { phase, currentRound, totalRounds, phaseTimeLeft, letter, pickerName, isPicker,
    categories, myAnswers, hasSubmitted, submittedCount, firstFinisher, firstFinisherName,
    allAnswers, hasVoted, votedCount, votingResults, answers: allPlayerAnswers,
    playerNames, players } = gameState;

  const [localTimer, setLocalTimer] = useState(phaseTimeLeft || 0);
  const [answers, setAnswers] = useState({});
  const [votes, setVotes] = useState({});
  const [animLetter, setAnimLetter] = useState('א');
  const [animDone, setAnimDone] = useState(false);
  const [voteCatIdx, setVoteCatIdx] = useState(0);
  const inputRefs = useRef({});
  const isHost = players?.find(p => p.id === playerId)?.isHost;

  useEffect(() => { setLocalTimer(phaseTimeLeft || 0); }, [phaseTimeLeft, phase]);
  useEffect(() => {
    if (phase === 'pick_letter' || phase === 'results' || phase === 'final_results') return;
    const iv = setInterval(() => {
      setLocalTimer(prev => {
        const next = Math.max(0, prev - 1);
        if (next === 5 || next === 3) playWarning();
        if (next === 0 && prev > 0) playTimeUp();
        return next;
      });
    }, 1000);
    return () => clearInterval(iv);
  }, [phase]);

  // Reset answers when new round
  useEffect(() => {
    if (phase === 'filling' || phase === 'countdown') {
      if (Object.keys(answers).length === 0 && myAnswers) setAnswers({...myAnswers});
    }
    if (phase === 'pick_letter') { setAnswers({}); setVotes({}); setAnimDone(false); setVoteCatIdx(0); }
    if (phase === 'voting') { setVoteCatIdx(0); }
    if (phase === 'filling') playPhaseStart();
    if (phase === 'final_results') playVictory();
  }, [phase, currentRound]);

  // Letter animation
  useEffect(() => {
    if (phase !== 'animating') return;
    setAnimDone(false);
    let i = 0;
    const fast = setInterval(() => { setAnimLetter(HEBREW_LETTERS[i % HEBREW_LETTERS.length]); i++; }, 80);
    const slowDown = setTimeout(() => {
      clearInterval(fast);
      let j = 0;
      const slow = setInterval(() => {
        setAnimLetter(HEBREW_LETTERS[(i + j) % HEBREW_LETTERS.length]); j++;
        if (j > 8) { clearInterval(slow); if (letter) setAnimLetter(letter); setAnimDone(true); playPhaseStart(); }
      }, 200);
    }, 2000);
    return () => { clearInterval(fast); clearTimeout(slowDown); };
  }, [phase, letter]);

  // Auto-submit when timer runs out
  useEffect(() => {
    if (localTimer === 0 && (phase === 'filling' || phase === 'countdown') && !hasSubmitted) {
      sendMessage('CC_SUBMIT_ANSWERS', { answers });
    }
  }, [localTimer, phase, hasSubmitted]);

  const updateAnswer = (key, val) => setAnswers(prev => ({...prev, [key]: val}));
  const allFilled = categories && categories.every(c => (answers[c.key] || '').trim().length > 0);

  const handleSubmit = () => {
    if (!allFilled) return;
    playClick();
    sendMessage('CC_SUBMIT_ANSWERS', { answers });
  };

  const handleVoteToggle = (pid, catKey) => {
    setVotes(prev => {
      const pv = prev[pid] || {};
      return {...prev, [pid]: {...pv, [catKey]: !pv[catKey]}};
    });
  };

  const submitVotes = () => {
    playClick();
    sendMessage('CC_SUBMIT_VOTES', { votes });
  };

  // ===== PICK LETTER =====
  if (phase === 'pick_letter') {
    return (
      <div className="card" style={{ direction: 'rtl', background: BG, textAlign: 'center' }}>
        <p style={{ color: M, marginBottom: '8px', fontSize: '0.85rem' }}>סיבוב {currentRound}/{totalRounds}</p>
        <h2 style={{ color: Y, fontSize: '1.4rem', marginBottom: '20px' }}>🌍 ארץ עיר</h2>
        {isPicker ? (
          <button onClick={() => { playClick(); sendMessage('CC_PICK_LETTER', {}); }} style={{
            padding: '20px 40px', fontSize: '1.3rem', fontWeight: 900, borderRadius: '20px', border: 'none',
            background: `linear-gradient(135deg, ${P}, #EC4899)`, color: 'white', cursor: 'pointer',
            fontFamily: 'Heebo', boxShadow: `0 0 30px ${P}60`, animation: 'pulse 1.5s infinite',
          }}>🎲 בחר אות!</button>
        ) : (
          <div style={{ padding: '30px', color: M, fontSize: '1.1rem' }}>
            <p>⏳ {pickerName} בוחר/ת אות...</p>
          </div>
        )}
        <style>{`@keyframes pulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.05); box-shadow: 0 0 40px ${P}80; } }`}</style>
      </div>
    );
  }

  // ===== ANIMATING =====
  if (phase === 'animating') {
    return (
      <div className="card" style={{ direction: 'rtl', background: BG, textAlign: 'center' }}>
        <p style={{ color: M, marginBottom: '16px' }}>🎲 בוחרים אות...</p>
        <div style={{
          width: '120px', height: '120px', margin: '0 auto 20px', borderRadius: '50%',
          background: animDone ? `linear-gradient(135deg, ${Y}, #F59E0B)` : `linear-gradient(135deg, ${P}, #EC4899)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '3.5rem', fontWeight: 900, color: 'white',
          boxShadow: animDone ? `0 0 40px ${Y}60` : `0 0 30px ${P}40`,
          transition: 'all 0.3s', animation: animDone ? 'popIn 0.4s ease' : 'none',
        }}>{animLetter}</div>
        {animDone && <p style={{ color: Y, fontSize: '1.3rem', fontWeight: 700 }}>האות: {letter}!</p>}
        <style>{`@keyframes popIn { 0% { transform: scale(0.5); } 60% { transform: scale(1.2); } 100% { transform: scale(1); } }`}</style>
      </div>
    );
  }

  // ===== FILLING / COUNTDOWN =====
  if (phase === 'filling' || phase === 'countdown') {
    const isCountdown = phase === 'countdown';
    const timer = localTimer;

    return (
      <div className="card" style={{ direction: 'rtl', background: BG }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
              width: '44px', height: '44px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: `linear-gradient(135deg, ${Y}, #F59E0B)`, fontSize: '1.5rem', fontWeight: 900, color: '#000',
            }}>{letter}</div>
            <span style={{ fontSize: '0.8rem', color: M }}>סיבוב {currentRound}/{totalRounds}</span>
          </div>
          <div style={{
            fontSize: '1.5rem', fontWeight: 900, color: isCountdown || timer <= 10 ? R : 'white',
            animation: (isCountdown || timer <= 10) ? 'pulse 0.6s infinite' : 'none',
          }}>⏱️ {timer}s</div>
        </div>

        {/* Countdown banner */}
        {isCountdown && firstFinisher && (
          <div style={{
            padding: '10px', marginBottom: '12px', borderRadius: '12px', textAlign: 'center',
            background: `${R}15`, border: `1px solid ${R}30`, color: R, fontWeight: 700, fontSize: '0.9rem',
            animation: 'pulse 1s infinite',
          }}>⚡ {hasSubmitted ? 'סיימת!' : `${firstFinisherName || 'מישהו'} סיים/ה! נשארו ${timer} שניות`}</div>
        )}

        {/* Category inputs */}
        {hasSubmitted ? (
          <div style={{ textAlign: 'center', padding: '30px', color: G }}>
            <p style={{ fontSize: '2rem', marginBottom: '8px' }}>✓</p>
            <p style={{ fontWeight: 700, fontSize: '1.1rem' }}>נשלח! ממתינים לשאר...</p>
            <p style={{ color: M, fontSize: '0.8rem', marginTop: '8px' }}>{submittedCount || 0} מתוך {players?.length || 0} סיימו</p>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '14px' }}>
              {categories?.map((cat, i) => (
                <div key={cat.key} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '1.1rem', minWidth: '28px' }}>{cat.icon}</span>
                  <span style={{ fontSize: '0.75rem', color: M, minWidth: '55px' }}>{cat.name}</span>
                  <input
                    ref={el => inputRefs.current[cat.key] = el}
                    value={answers[cat.key] || ''}
                    onChange={e => updateAnswer(cat.key, e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        const nextCat = categories[i + 1];
                        if (nextCat) inputRefs.current[nextCat.key]?.focus();
                        else if (allFilled) handleSubmit();
                      }
                    }}
                    placeholder={`${cat.name} ב-${letter}...`}
                    style={{
                      flex: 1, padding: '10px 12px', borderRadius: '10px', fontSize: '0.95rem',
                      background: 'rgba(255,255,255,0.06)', border: `1px solid ${(answers[cat.key] || '').trim() ? G + '40' : 'rgba(255,255,255,0.1)'}`,
                      color: 'white', fontFamily: 'Heebo', direction: 'rtl', outline: 'none',
                    }}
                  />
                  {(answers[cat.key] || '').trim() && <span style={{ color: G, fontSize: '0.9rem' }}>✓</span>}
                </div>
              ))}
            </div>
            <button onClick={handleSubmit} disabled={!allFilled} style={{
              width: '100%', padding: '14px', borderRadius: '14px', border: 'none', fontSize: '1.05rem', fontWeight: 700,
              background: allFilled ? `linear-gradient(135deg, ${G}, #10B981)` : 'rgba(255,255,255,0.06)',
              color: 'white', cursor: allFilled ? 'pointer' : 'not-allowed', fontFamily: 'Heebo',
              opacity: allFilled ? 1 : 0.4, boxShadow: allFilled ? `0 4px 20px ${G}40` : 'none',
            }}>סיימתי! ✓</button>
          </>
        )}
        {error && <p style={{ color: R, textAlign: 'center', marginTop: '8px', fontSize: '0.85rem' }}>{error}</p>}
        <style>{`@keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.6; } }`}</style>
      </div>
    );
  }

  // ===== VOTING (one category at a time) =====
  if (phase === 'voting') {
    if (hasVoted) {
      return (
        <div className="card" style={{ direction: 'rtl', background: BG, textAlign: 'center' }}>
          <p style={{ fontSize: '2rem', marginBottom: '12px' }}>✓</p>
          <p style={{ color: G, fontWeight: 700, fontSize: '1.1rem' }}>סיימת לבדוק!</p>
          <p style={{ color: M, fontSize: '0.85rem', marginTop: '8px' }}>ממתינים לשאר השחקנים... ({votedCount || 0}/{players?.length || 0})</p>
        </div>
      );
    }

    const cat = categories?.[voteCatIdx];
    const isLastCat = voteCatIdx >= (categories?.length || 8) - 1;
    const otherPlayers = allAnswers ? Object.entries(allAnswers).filter(([pid]) => pid !== playerId) : [];
    const myAns = allAnswers?.[playerId]?.answers?.[cat?.key] || '';

    return (
      <div className="card" style={{ direction: 'rtl', background: BG }}>
        {/* Progress bar */}
        <div style={{ display: 'flex', gap: '3px', marginBottom: '14px' }}>
          {categories?.map((c, i) => (
            <div key={c.key} style={{ flex: 1, height: '4px', borderRadius: '2px', background: i <= voteCatIdx ? Y : 'rgba(255,255,255,0.1)', transition: 'background 0.3s' }} />
          ))}
        </div>

        <div style={{ textAlign: 'center', marginBottom: '14px' }}>
          <span style={{ fontSize: '0.75rem', color: M }}>קטגוריה {voteCatIdx + 1} מתוך {categories?.length || 8}</span>
          <h3 style={{ color: Y, fontSize: '1.3rem', marginTop: '4px' }}>{cat?.icon} {cat?.name}</h3>
          <span style={{ fontSize: '0.8rem', color: M }}>האות: <b style={{ color: Y, fontSize: '1rem' }}>{letter}</b></span>
        </div>

        {/* My answer */}
        <div style={{ padding: '10px 14px', marginBottom: '10px', borderRadius: '12px', background: `${P}15`, border: `1px solid ${P}30` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '0.8rem', color: P, fontWeight: 700 }}>התשובה שלי:</span>
            <span style={{ color: 'white', fontWeight: 600 }}>{myAns || '—'}</span>
          </div>
        </div>

        {/* Other players' answers */}
        {otherPlayers.map(([pid, data]) => {
          const ans = data.answers?.[cat?.key] || '';
          const voted = votes[pid]?.[cat?.key];
          const isValid = voted === undefined || voted === true;
          return (
            <div key={pid} style={{ padding: '12px 14px', marginBottom: '8px', borderRadius: '12px', background: 'rgba(255,255,255,0.04)', border: `1px solid ${voted === false ? R + '40' : 'rgba(255,255,255,0.08)'}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ fontWeight: 700, color: 'white' }}>{data.name}</span>
                <span style={{ fontSize: '1rem', color: ans ? 'white' : '#555', fontWeight: 600 }}>{ans || '—'}</span>
              </div>
              {ans && (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={() => setVotes(prev => ({...prev, [pid]: {...(prev[pid] || {}), [cat.key]: true}}))} style={{
                    flex: 1, padding: '10px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 700,
                    background: isValid ? `${G}25` : 'rgba(255,255,255,0.04)',
                    color: isValid ? G : M, fontFamily: 'Heebo', transition: 'all 0.2s',
                  }}>✓ תקין</button>
                  <button onClick={() => setVotes(prev => ({...prev, [pid]: {...(prev[pid] || {}), [cat.key]: false}}))} style={{
                    flex: 1, padding: '10px', borderRadius: '10px', border: 'none', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 700,
                    background: voted === false ? `${R}25` : 'rgba(255,255,255,0.04)',
                    color: voted === false ? R : M, fontFamily: 'Heebo', transition: 'all 0.2s',
                  }}>✗ לא תקין</button>
                </div>
              )}
            </div>
          );
        })}

        {/* Next / Submit button */}
        {!isLastCat ? (
          <button onClick={() => { playClick(); setVoteCatIdx(prev => prev + 1); }} style={{
            width: '100%', padding: '14px', borderRadius: '14px', border: 'none', fontSize: '1rem', fontWeight: 700,
            background: `linear-gradient(135deg, ${P}, #7C3AED)`, color: 'white', cursor: 'pointer', fontFamily: 'Heebo',
            marginTop: '8px',
          }}>הבא → {categories?.[voteCatIdx + 1]?.icon} {categories?.[voteCatIdx + 1]?.name}</button>
        ) : (
          <button onClick={submitVotes} style={{
            width: '100%', padding: '14px', borderRadius: '14px', border: 'none', fontSize: '1rem', fontWeight: 700,
            background: `linear-gradient(135deg, ${G}, #10B981)`, color: 'white', cursor: 'pointer', fontFamily: 'Heebo',
            marginTop: '8px',
        }}>✓ סיימתי לבדוק - שלח</button>
        )}

        {/* Back button */}
        {voteCatIdx > 0 && !isLastCat && (
          <button onClick={() => setVoteCatIdx(prev => prev - 1)} style={{
            width: '100%', padding: '10px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.1)',
            background: 'transparent', color: M, cursor: 'pointer', fontFamily: 'Heebo', fontSize: '0.85rem', marginTop: '6px',
          }}>← חזרה</button>
        )}

        {error && <p style={{ color: R, textAlign: 'center', marginTop: '8px', fontSize: '0.85rem' }}>{error}</p>}
      </div>
    );
  }

  // ===== RESULTS =====
  if (phase === 'results') {
    return (
      <div className="card" style={{ direction: 'rtl', background: BG }}>
        <h3 style={{ textAlign: 'center', color: Y, marginBottom: '14px' }}>📊 תוצאות סיבוב {currentRound}</h3>
        <p style={{ textAlign: 'center', color: M, fontSize: '0.8rem', marginBottom: '14px' }}>האות: <b style={{ color: Y }}>{letter}</b></p>

        {votingResults && Object.entries(votingResults).map(([pid, data]) => {
          const pName = playerNames?.[pid] || players?.find(p => p.id === pid)?.name || '?';
          const total = data._roundTotal || 0;
          const bonus = data._firstFinisherBonus || 0;
          return (
            <div key={pid} style={{ marginBottom: '12px', padding: '12px', borderRadius: '14px', background: 'rgba(255,255,255,0.03)', border: `1px solid rgba(255,255,255,0.06)` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontWeight: 700, color: pid === playerId ? P : 'white' }}>{pName} {pid === playerId && '(אני)'}</span>
                <span style={{ fontWeight: 900, color: Y }}>{total} נק' {bonus > 0 && <span style={{ color: G, fontSize: '0.75rem' }}>+{bonus} בונוס ⚡</span>}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
                {categories?.map(cat => {
                  const catResult = data[cat.key];
                  if (!catResult) return null;
                  const pts = catResult.points;
                  const ans = allPlayerAnswers?.[pid]?.[cat.key] || '';
                  const valid = catResult.status !== 'invalid' && catResult.status !== 'empty' && catResult.status !== 'wrong_letter';
                  return (
                    <div key={cat.key} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '3px 6px', borderRadius: '6px', background: pts === 10 ? `${Y}10` : pts === 5 ? `${P}10` : `${R}08`, fontSize: '0.72rem' }}>
                      <span>{cat.icon}</span>
                      <span style={{ color: valid ? 'white' : '#555', textDecoration: valid ? 'none' : 'line-through' }}>{ans || '—'}</span>
                      <span style={{ marginRight: 'auto', fontWeight: 700, color: pts === 10 ? Y : pts === 5 ? P : R }}>{pts}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // ===== FINAL RESULTS =====
  if (phase === 'final_results') {
    const sorted = players ? [...players].sort((a, b) => b.score - a.score) : [];
    const winner = sorted[0];
    return (
      <div className="card" style={{ direction: 'rtl', background: BG }}>
        <div style={{ textAlign: 'center', padding: '20px', marginBottom: '20px', background: `${Y}10`, borderRadius: '16px', border: `1px solid ${Y}25` }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '8px' }}>🏆</div>
          <h2 style={{ fontSize: '1.3rem', marginBottom: '6px' }}>המנצח!</h2>
          {winner && <p style={{ fontSize: '1.5rem', color: Y, fontWeight: 700 }}>{winner.avatar} {winner.name} - {winner.score} נק'</p>}
        </div>
        <div style={{ marginBottom: '16px' }}>
          {sorted.map((p, i) => (
            <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', marginBottom: '4px', borderRadius: '10px', background: i === 0 ? `${Y}12` : 'rgba(255,255,255,0.03)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>{i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `${i+1}.`}</span>
                <span style={{ fontWeight: i < 3 ? 700 : 400 }}>{p.name} {p.id === playerId && <span style={{ color: M, fontSize: '0.7rem' }}>(אני)</span>}</span>
              </div>
              <span style={{ fontWeight: 700, color: Y }}>{p.score}</span>
            </div>
          ))}
        </div>
        {isHost && (
          <>
            <button className="btn btn-primary" onClick={() => { clearError(); sendMessage('NEXT_ROUND', {}); }} style={{ marginBottom: '8px' }}>🔤 אות חדשה!</button>
            <button className="btn btn-ghost" onClick={() => { clearError(); sendMessage('NEW_GAME', {}); }}>🏠 חזרה ללובי</button>
          </>
        )}
        {!isHost && <p style={{ textAlign: 'center', color: M, marginTop: '12px' }}>ממתינים שהמנהל ימשיך...</p>}
      </div>
    );
  }

  return <div className="card" style={{ background: BG, textAlign: 'center' }}><p style={{ color: M }}>טוען...</p></div>;
}

export default CountryCity;
